import React from "react";
import { Link } from "react-router-dom";
import { Stethoscope, ClipboardList, LineChart, Users, Download, Target, ArrowRight } from "lucide-react";
import InstallPrompt from "@/components/InstallPrompt";

const FEATURES = [
  { icon: ClipboardList, title: "Structured Case Logging", body: "Capture every case with CPT/ICD codes, attending, role, approach, and outcomes." },
  { icon: Users, title: "Attending Tracking", body: "Automatically build your list of attendings and institutions as you log." },
  { icon: LineChart, title: "Monthly Analytics", body: "Trend charts, role breakdowns, and complication rates at a glance." },
  { icon: Target, title: "Board Prep Progress", body: "Track cases toward your board target with projected completion date." },
  { icon: Download, title: "Excel & PDF Export", body: "Download your case log for portfolios, reviews, or credentialing." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-neu-bg text-neu-text">
      <InstallPrompt />
      <header className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-neu-accent flex items-center justify-center shadow-lg shadow-neu-accent/30">
            <Stethoscope className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="heading text-xl leading-none">FellowLog</div>
            <div className="text-xs subtle mt-1">Surgical case log</div>
          </div>
        </div>
        <nav className="flex items-center gap-3">
          <Link to="/signin" className="btn-neu" data-testid="landing-signin-btn">
            Sign In
          </Link>
          <Link to="/signup" className="btn-accent hidden sm:inline-flex" data-testid="landing-signup-btn">
            Sign Up
          </Link>
        </nav>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-16 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="chip mb-6">
            <span className="w-2 h-2 rounded-full bg-neu-accent" /> Built for surgical fellows
          </span>
          <h1 className="heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#3F4E5B]">
            Track your surgical journey through fellowship.
          </h1>
          <p className="text-base md:text-lg subtle mt-6 max-w-xl leading-relaxed">
            FellowLog is your personal surgical case log — capture cases in seconds, follow your
            progress across attendings and procedures, and stay on pace for board certification.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/signup"
              className="btn-accent inline-flex items-center gap-2"
              data-testid="landing-hero-cta"
            >
              Create account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/signin" className="btn-neu" data-testid="landing-hero-signin">
              Sign in
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
            {[
              { k: "Fellow", v: "Personal case log" },
              { k: "10+", v: "Procedure categories" },
              { k: "PDF/Excel", v: "Export ready" },
            ].map((s) => (
              <div key={s.v} className="neu-card-sm p-4">
                <div className="heading text-lg text-neu-accent">{s.k}</div>
                <div className="text-xs subtle mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="neu-card p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs subtle uppercase tracking-wider">Today</div>
                <div className="heading text-2xl">Ortho fellowship, PGY-6</div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-neu-bg flex items-center justify-center shadow-neu-inset-sm">
                <Stethoscope className="w-6 h-6 text-neu-accent" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { l: "Total cases", v: "142" },
                { l: "This month", v: "23" },
                { l: "Primary %", v: "48%" },
                { l: "Complications", v: "3.2%" },
              ].map((k) => (
                <div key={k.l} className="neu-inset p-4">
                  <div className="text-xs subtle uppercase">{k.l}</div>
                  <div className="heading text-2xl text-[#3F4E5B] mt-1">{k.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <div className="flex items-center justify-between text-xs subtle mb-2">
                <span>Board progress</span>
                <span>142 / 300</span>
              </div>
              <div className="h-3 rounded-full bg-neu-bg shadow-neu-inset-sm overflow-hidden">
                <div className="h-full bg-neu-accent" style={{ width: "47%" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <h2 className="heading text-3xl md:text-4xl text-[#3F4E5B] max-w-2xl">
          Everything a fellow needs. Nothing they don&apos;t.
        </h2>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {FEATURES.map((f) => (
            <div key={f.title} className="neu-card p-7">
              <div className="w-12 h-12 rounded-xl bg-neu-bg flex items-center justify-center shadow-neu-inset-sm mb-5">
                <f.icon className="w-6 h-6 text-neu-accent" />
              </div>
              <div className="heading text-lg text-[#3F4E5B]">{f.title}</div>
              <p className="text-sm subtle mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 md:px-10 py-10 border-t border-neu-muted/20 text-sm subtle flex flex-col md:flex-row items-center justify-between gap-4">
        <div>© {new Date().getFullYear()} FellowLog — a personal surgical case log</div>
        <div className="flex gap-6">
          <Link to="/signin" className="hover:text-neu-accent">Sign in</Link>
          <Link to="/signup" className="hover:text-neu-accent">Get started</Link>
        </div>
      </footer>
    </div>
  );
}
